import React, { useEffect, useState, useCallback } from "react";
import { adminApi, formatApiError } from "../../../context/AuthContext";
import { Button } from "../../ui/button";
import { toast } from "sonner";
import { RefreshCw } from "lucide-react";

const MODULE_LABELS = {
  auth: "Authenticatie",
  users: "Medewerkers",
  settings: "Instellingen",
  quotes: "Offertes",
  customers: "Klanten",
  pickup: "Pickup & Delivery",
  reminders: "Reminders",
  website: "Websitebeheer",
  webshop: "Webshopbeheer",
};

const ACTION_LABELS = {
  login_success: "Login geslaagd",
  login_failed: "Login mislukt",
  login_blocked_disabled: "Login geblokkeerd (gedeactiveerd)",
  user_created: "Gebruiker aangemaakt",
  user_disabled: "Gebruiker gedeactiveerd",
  user_enabled: "Gebruiker geactiveerd",
  user_role_changed: "Rol gewijzigd",
  user_updated: "Gebruiker bijgewerkt",
  user_deleted: "Gebruiker verwijderd",
  password_reset: "Wachtwoord gereset",
  setting_changed: "Instelling gewijzigd",
};

function PrettyValue({ value }) {
  if (value === null || value === undefined) return <span className="text-gray-400">—</span>;
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") return String(value);
  return (
    <pre className="text-[10px] bg-gray-50 border border-gray-200 p-1 inline-block max-w-full overflow-x-auto">
      {JSON.stringify(value, null, 0)}
    </pre>
  );
}

export default function AuditSection() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await adminApi.get("/admin/audit-logs", { params: { limit: 200 } });
      setItems(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="admin-section-audit">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="font-chivo font-black text-3xl text-[#0A192F] tracking-tight">Activiteitenlogboek</h1>
          <p className="text-sm text-gray-600 mt-1">
            Logging van inlogpogingen, account- en instellingswijzigingen. Laatste 200 entries.
          </p>
        </div>
        <Button onClick={load} variant="outline" size="sm" className="rounded-none" data-testid="audit-refresh">
          <RefreshCw className="w-4 h-4 mr-1" /> Vernieuwen
        </Button>
      </div>

      <div className="mt-6 bg-white border border-gray-200 overflow-x-auto">
        {loading ? (
          <div className="p-6 text-gray-500">Laden...</div>
        ) : items.length === 0 ? (
          <div className="p-8 text-center text-gray-500">Nog geen log-entries.</div>
        ) : (
          <table className="w-full text-xs">
            <thead className="bg-[#F8F9FA] border-b border-gray-200 text-left">
              <tr>
                <th className="p-3 font-chivo font-bold uppercase tracking-wide">Datum / tijd</th>
                <th className="p-3 font-chivo font-bold uppercase tracking-wide">Gebruiker</th>
                <th className="p-3 font-chivo font-bold uppercase tracking-wide">Module</th>
                <th className="p-3 font-chivo font-bold uppercase tracking-wide">Actie</th>
                <th className="p-3 font-chivo font-bold uppercase tracking-wide">Doel</th>
                <th className="p-3 font-chivo font-bold uppercase tracking-wide">Oude waarde</th>
                <th className="p-3 font-chivo font-bold uppercase tracking-wide">Nieuwe waarde</th>
                <th className="p-3 font-chivo font-bold uppercase tracking-wide">IP</th>
                <th className="p-3 font-chivo font-bold uppercase tracking-wide">Status</th>
              </tr>
            </thead>
            <tbody>
              {items.map((row) => (
                <tr key={row.id} className="border-b border-gray-100" data-testid={`audit-row-${row.id}`}>
                  <td className="p-3 whitespace-nowrap text-gray-600">
                    {row.created_at?.slice(0, 19).replace("T", " ")}
                  </td>
                  <td className="p-3">
                    <div className="font-medium text-[#0A192F]">{row.actor_email || "—"}</div>
                    <div className="text-[10px] uppercase tracking-wide text-gray-400">{row.actor_role || "anoniem"}</div>
                  </td>
                  <td className="p-3">
                    <span className="inline-block bg-gray-100 text-gray-700 px-2 py-0.5 text-[10px] uppercase tracking-wide font-bold">
                      {MODULE_LABELS[row.module] || row.module}
                    </span>
                  </td>
                  <td className="p-3 font-medium">{ACTION_LABELS[row.action] || row.action}</td>
                  <td className="p-3 text-gray-600">{row.target_label || "—"}</td>
                  <td className="p-3"><PrettyValue value={row.old_value} /></td>
                  <td className="p-3"><PrettyValue value={row.new_value} /></td>
                  <td className="p-3 text-gray-500 whitespace-nowrap">{row.ip || "—"}</td>
                  <td className="p-3">
                    {row.success ? (
                      <span className="inline-block bg-green-100 text-green-700 px-2 py-0.5 text-[10px] uppercase tracking-wide font-bold">OK</span>
                    ) : (
                      <span className="inline-block bg-red-100 text-red-700 px-2 py-0.5 text-[10px] uppercase tracking-wide font-bold">Mislukt</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
